/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {
  useDispatch,
  useSelector
} from 'react-redux';
import {useIsDesktop} from '@ion/dls/src/hooks/useMedia';
import {isCurrentEnabled} from '@ion/dls/src/maps/layers/LayerChooser';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {Route} from '@/routes/RouteDefinitions';
import {
  exitFindNearby,
  GET_ASSOCIATED_ASSETS_ERROR,
  SET_ASSET_DETAIL_VIEW,
  SET_FIND_NEARBY_ASSET,
  setAssetSelected,
  setFindButtonClickStatus,
  setFindNearbyPopup,
  setSelectedAsset,
  setSelectedHistoryAsset,
  setTool,
  setToolDetailView,
  setTrackingMapIsAssetDetailHistory
} from '@/store/actions/tracking';
import {mapLayerSelector, selectedAssetSelector} from '@/store/selectors/tracking-selectors';
import {BreadcrumbsType, SidesheetPages} from '@/types/sidesheet';

/**
 * @useShowCrumbs
 * React Hook to provide util functions for resetting and selecting assets.
 * It also provides resetting tools, find near by, icon click on breadcrumbs.
 *
 *  ```typescript
 *   import {useSelectAssetAndResets} from '@/routes/tracking/hooks';
 *
 *   const [handleSelectedAsset, resetTracking, resetToolsAndFindNearBy, onIconClick]
 *       = useSelectAssetAndResets();
 *  ```
 */
export const useSelectAssetAndResets = () => {
  const dispatch      = useDispatch();
  const isDesktop     = useIsDesktop();
  const layers        = useSelector(mapLayerSelector);
  const selectedAsset = useSelector(selectedAssetSelector);

  const resetToolsAndFindNearBy = useCallback(function(updateAssetList: boolean = false, route = Route.TRACKING) {
    resetTools();
    resetFindNearBy(updateAssetList, route);
  }, [isDesktop]);

  const resetTools = useCallback(function() {
    dispatch(setTool(null));
    dispatch(setToolDetailView(false));
  }, []);

  const resetFindNearBy = useCallback(function(updateAssetList: boolean = false, route = Route.TRACKING) {
    const omitExitFindNearbyV2 = route === Route.TRACKING2;
    dispatch(setFindButtonClickStatus(false));
    dispatch(setFindNearbyPopup(false));
    dispatch({type: SET_FIND_NEARBY_ASSET, value: null});
    dispatch(exitFindNearby(updateAssetList, omitExitFindNearbyV2) as any);
  }, [isDesktop]);

  const resetTracking = useCallback(function(fromRoot = false, keepSelectedAsset = false) {
    if (!keepSelectedAsset) {
      dispatch(setAssetSelected());
    }
    dispatch(setTrackingMapIsAssetDetailHistory(false));
    dispatch({type: SET_ASSET_DETAIL_VIEW, value: false});
    dispatch({type: GET_ASSOCIATED_ASSETS_ERROR, error: null, defaultAssets: []});
    fromRoot && handleSelectedAsset(null);
  }, []);

  const resetSideSheet = useCallback(() => {
    resetTracking(true);
    resetToolsAndFindNearBy();
  }, [isDesktop]);

  const handleSelectedAsset = useCallback(function(value) {
    if (isCurrentEnabled(layers)) {
      dispatch(setSelectedAsset({asset: value}) as any);
    } else {
      dispatch(setSelectedHistoryAsset(value));
    }
  }, [layers]);

  const onIconClick = useCallback(function(crumbs: BreadcrumbsType[], route = Route.TRACKING) {
    const lastCrumb: BreadcrumbsType =  crumbs?.at(-1);
    if ((lastCrumb?.source === SidesheetPages.FIND_NEARBY) ||
      (lastCrumb?.source === SidesheetPages.DETAIL_FIND_NEARBY)) {
      resetToolsAndFindNearBy(true, route);
      if (selectedAsset) {
        const assetCrumb = crumbs?.at(-2);
        assetCrumb?.source === SidesheetPages.DETAIL && dispatch({type: SET_ASSET_DETAIL_VIEW, value: true});
      } else {
        resetTracking(true);
      }
    } else if (lastCrumb?.source === SidesheetPages.ASSOCIATED_TAG) {
      resetToolsAndFindNearBy(false, route);
      selectedAsset && dispatch({type: SET_ASSET_DETAIL_VIEW, value: true});
    } else {
      // current theme: the back arrow out of asset details keeps the asset
      // selected, so its card stays highlighted in the list - a breadcrumb
      // deselects it. The legacy theme deselects it either way, as before ...
      resetTracking(false, isCurrentTheme() && lastCrumb?.source === SidesheetPages.DETAIL);
      resetToolsAndFindNearBy(false, route);
    }
  }, [selectedAsset, isDesktop]);

  return [
    handleSelectedAsset,
    resetTracking,
    resetToolsAndFindNearBy,
    onIconClick,
    resetSideSheet
  ];
};
